import { useCallback, useEffect, useState } from "react";
import { API_BASE, apiFetch } from "../api";

const ENDPOINTS = {
  bills: "bills",
  income: "income",
  loans: "loans",
  cards: "credit-cards",
  assets: "assets",
  // One row per bill on Bills & Payments (not per due date), for past payments.
  billers: "billers",
  // The monthly budget and one-time ones (trips, gadgets).
  budgets: "budgets",
};

const EMPTY = { bills: [], income: [], loans: [], cards: [], assets: [], billers: [], budgets: [] };

// Loads everything the phone screens need in one go. `patch` updates the local
// copy straight away so taps feel instant; `reload` re-syncs with the server.
export const useMobileData = () => {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(() => {
    setError(false);
    const keys = Object.keys(ENDPOINTS);
    return Promise.all(
      keys.map((k) => apiFetch(`${API_BASE}/${ENDPOINTS[k]}`).then((res) => (res.ok ? res.json() : Promise.reject())))
    )
      .then((results) => setData(Object.fromEntries(keys.map((k, i) => [k, results[i]]))))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const send = useCallback(
    (collection, method, id, body) =>
      apiFetch(`${API_BASE}/${ENDPOINTS[collection]}${id ? `/${id}` : ""}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      }).then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Something went wrong.");
        }
        return res.status === 204 ? null : res.json();
      }),
    []
  );

  const update = (collection, id, changes) => {
    setData((d) => ({ ...d, [collection]: d[collection].map((r) => (r.id === id ? { ...r, ...changes } : r)) }));
    return send(collection, "PATCH", id, changes)
      .then((row) => {
        // Turning a bill into an Investments one can add an investment on the server,
        // paying a repeating bill lines up its next one there, and paying (or
        // unpaying, or changing) an instalment changes its investment's value.
        // So does the account or card it was paid from.
        if (collection === "bills" && (changes.type === "Investments" || changes.status !== undefined || (row && (row.sourceKind === "asset" || row.paidFromKind)))) reload();
        return row;
      })
      .catch(reload);
  };

  const remove = (collection, id) => {
    setData((d) => ({ ...d, [collection]: d[collection].filter((r) => r.id !== id) }));
    return send(collection, "DELETE", id)
      // A deleted paid instalment comes back off its investment's value.
      .then(() => collection === "bills" && reload())
      .catch(reload);
  };

  const create = (collection, body) =>
    send(collection, "POST", null, body).then((row) => {
      // The monthly budget is replaced rather than added to (there's only one).
      setData((d) => ({ ...d, [collection]: [...d[collection].filter((r) => r.id !== row.id), row] }));
      // An Investments bill is linked to an investment (made if needed) and may
      // line up its next instalment; a repeating one gets (or joins) a biller and a
      // past payment of a biller may have settled one of its entries. Fetch those too.
      if (collection === "bills" && (row.sourceKind || row.biller_id || row.paidFromKind)) reload();
      return row;
    });

  // Actions that change several things on the server at once; they reload after.
  const post = (path, body) =>
    apiFetch(`${API_BASE}/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) }).then(async (res) => {
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Something went wrong.");
      }
      await reload();
    });

  // Money lent (a "Lent" expense) came back, into `receivedIntoId` if given: the
  // server records it as money received.
  const markRepaid = (billId, body = {}) => post(`bills/${billId}/repaid`, body);
  // Money borrowed ("Borrowed" income) paid back, from { paidFromKind, paidFromId }.
  const repayBorrowed = (incomeId, body = {}) => post(`income/${incomeId}/repaid`, body);
  // A credit card's bill paid, { amount, fromAccountId, date }.
  const payCard = (cardId, body) => post(`credit-cards/${cardId}/pay`, body);

  return { ...data, loading, error, reload, update, remove, create, markRepaid, repayBorrowed, payCard };
};
